/** Remove only parentheses, dashes, spaces, and periods (the separators we ignore for detection). */
export function stripIgnoredPhoneSeparators(value: string): string {
    return value.replace(/[()\-\s.]/g, '');
}

/** Extract digits only; strips leading + before digit extraction. */
export function extractPhoneDigits(value: string): string {
    let normalized = stripIgnoredPhoneSeparators(value);
    if (normalized.startsWith('+')) {
        normalized = normalized.slice(1);
    }
    return normalized.replace(/\D/g, '');
}

/**
 * Normalize to 10-digit US national number, or null when not a formattable US phone.
 * Accepts 10 digits, 11 digits with leading 1, or +1 country prefix.
 * Rejects values with non-separator, non-digit characters (e.g. 'x' for extensions).
 */
export function toTenDigitPhoneCore(value: string): string | null {
    let stripped = stripIgnoredPhoneSeparators(value);
    if (stripped.startsWith('+')) stripped = stripped.slice(1);
    if (/\D/.test(stripped)) return null;
    if (/^\d{10}$/.test(stripped)) return stripped;
    if (/^1\d{10}$/.test(stripped)) return stripped.slice(1);
    return null;
}

/**
 * True when the value is a US phone: 10 digits, 11 digits with leading 1, or +1 prefix
 * (parentheses, dashes, spaces, and periods are ignored).
 */
export function isTenDigitPhoneValue(value: string): boolean {
    return toTenDigitPhoneCore(value) != null;
}

/** Format a US phone as (xxx) xxx-xxxx. */
export function formatTenDigitPhone(value: string): string {
    const core = toTenDigitPhoneCore(value);
    if (!core) return value;
    return `(${core.slice(0, 3)}) ${core.slice(3, 6)}-${core.slice(6)}`;
}

/**
 * Format a phone field value for display when the label contains "phone" (case-insensitive)
 * and the value is a formattable US number. Otherwise returns the original string.
 */
export function formatPhoneDisplayIfApplicable(label: string, value: unknown): string {
    const str = value != null && value !== '' ? String(value) : '';
    if (!str || !/phone/i.test(label ?? '')) return str;
    if (!isTenDigitPhoneValue(str)) return str;
    return formatTenDigitPhone(str);
}
